<script>
    import PhotoGalleryDropZone from './PhotoGalleryDropZone.svelte';
    import ApiClient from '../../scripts/apiclient';
    import { createActionRunner } from '../../scripts/ActionRunner';

    export let playground;

    /**
     * Shows the photos without any way of changing them, for dialogs that display them to
     * judge the playground by rather than to handle them one by one.
     */
    export let readonly = false;
    let photos = [];
    $: updatePhotos(playground);

    const { busy, run } = createActionRunner();

    /**
     * The photo whose action is running, and the name of that action. Used to keep the
     * control buttons of that photo visible and to put the spinner on the right button.
     */
    let busyPhoto = null;
    let busyAction = null;

    async function updatePhotos(playground) {
        photos = await loadPhotos(playground);
    }

    async function loadPhotos(playground) {
        if (!playground) {
            return [];
        }

        let photos = await ApiClient.loadPhotos(playground._id);
        return photos;
    }

    async function reloadPhotos() {
        photos = await loadPhotos(playground);
        await reloadPlayground();
    }

    async function reloadPlayground() {
        let updated = await ApiClient.loadPlayground(playground._id);
        // Use the assignment here to trigger a Svelte update
        playground = Object.assign(playground, updated);
    }

    const uploadPhotos = run(async (files) => {
        let accepted = files ? files.accepted : [];

        if (!playground || !accepted || accepted.length == 0) {
            return;
        }

        await ApiClient.uploadPhotos(playground, accepted);
        await reloadPlayground();
    });

    /**
     * Wraps an action on a single photo, so that the gallery is busy while it runs and the
     * photo it belongs to can show that it is being handled.
     *
     * @param {String} name The name of the action, used to place the spinner.
     * @param {Function} action The action to run, taking the photo as its argument.
     * @returns {Function} The wrapped handler.
     */
    function runPhotoAction(name, action) {
        return run(async (photo) => {
            busyPhoto = photo;
            busyAction = name;

            try {
                await action(photo);
            } finally {
                busyPhoto = null;
                busyAction = null;
            }
        });
    }

    const approvePhoto = runPhotoAction('approve', async (photo) => {
        await ApiClient.approvePhoto(photo);
        await reloadPhotos();
    });

    const rejectPhoto = runPhotoAction('reject', async (photo) => {
        if (photo.state == 'published' && !confirm('Arkivera fotot?')) {
            return;
        }

        await ApiClient.rejectPhoto(photo);
        await reloadPhotos();
    });

    const draftPhoto = runPhotoAction('draft', async (photo) => {
        await ApiClient.draftPhoto(photo);
        await reloadPhotos();
    });
</script>

{#if !readonly || photos.length > 0}
<div class="container">
    {#if !readonly}
        <PhotoGalleryDropZone filesSelected={uploadPhotos} />
    {/if}
    {#each photos as photo}
        <div class="wrapper photo-{photo.state}">
            <img
                src={ApiClient.getThumbnailUrl(photo._id, 100)}
                width="100"
                height="100"
                alt="Foto av lekplatsen"
                title={photo.state == 'draft' ? 'Utkast' : 'Foto av lekplatsen'}
            />
            {#if !readonly}
                <!-- The buttons stay visible while the photo is handled, so the spinner doesn't
                     disappear when the mouse leaves the photo -->
                <div class="control-buttons" class:busy={busyPhoto === photo}>
                    {#if photo.state == 'published'}
                        <button class="draft" disabled={!!$busy} on:click={() => draftPhoto(photo)} title="Sätt till förslag">
                            {#if busyPhoto === photo && busyAction == 'draft'}
                                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            {:else}
                                <i class="fas fa-question" />
                            {/if}
                        </button>
                    {/if}
                    {#if photo.state != 'removed'}
                        <button class="reject" disabled={!!$busy} on:click={() => rejectPhoto(photo)} title="Arkivera foto">
                            {#if busyPhoto === photo && busyAction == 'reject'}
                                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            {:else}
                                <i class="fas fa-ban" />
                            {/if}
                        </button>
                    {/if}
                    {#if photo.state != 'published'}
                        <button class="approve" disabled={!!$busy} on:click={() => approvePhoto(photo)} title="Publicera foto">
                            {#if busyPhoto === photo && busyAction == 'approve'}
                                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            {:else}
                                <i class="fas fa-check" />
                            {/if}
                        </button>
                    {/if}
                </div>
            {/if}
        </div>
    {/each}
</div>
{/if}

<style>
    .container {
        padding: 0;
        overflow-x: auto;
        height: 100px;
        white-space: nowrap;
        background: #eee;
    }

    .wrapper {
        display: inline-block;
        position: relative;
        vertical-align: top;
        margin-right: 5px;
    }

    .control-buttons {
        position: absolute;
        left: 0;
        top: 35px;
        right: 0;
        height: 30px;
        text-align: center;
        visibility: hidden;
    }

    .wrapper:hover .control-buttons,
    .control-buttons.busy {
        visibility: visible;
    }

    .wrapper .control-buttons button {
        width: 30px;
        height: 30px;
        border-radius: 15px;
        border: none;
    }

    .photo-draft,
    .photo-removed {
        background: url('/assets/diagonal-bg.png');
    }

    .photo-draft img,
    .photo-removed img {
        opacity: 85%;
    }

    .control-buttons button:hover:enabled {
        background: white;
    }

    .control-buttons button:disabled {
        cursor: default;
    }

    .control-buttons .spinner-border {
        width: 14px;
        height: 14px;
        border-width: 2px;
    }

    .approve:hover:enabled {
        color: green;
    }

    .reject:hover:enabled {
        color: red;
    }

    .draft:hover:enabled {
        color: orange;
    }
</style>
